import React, { useState } from 'react';
import MessageIcon from '@mui/icons-material/Message';
import { Paper, Skeleton, List, ListItemButton, ListItemText, Button } from '@mui/material';
import { Box, Typography } from 'components/mui';
import type { RecentActivityItem } from 'containers/pages/cantrackDashboard/types';

const INITIAL_DISPLAY = 8;

interface RecentActivitySectionProps {
  activity: RecentActivityItem[];
  loading: boolean;
  onCaseClick: (caseId: number) => void;
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function RecentActivitySection({ activity, loading, onCaseClick }: RecentActivitySectionProps) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Recent Activity
        </Typography>
        {Array.from({ length: 5 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="50%" />
            </Box>
          </Box>
        ))}
      </Paper>
    );
  }

  const visibleActivity = expanded ? activity : activity.slice(0, INITIAL_DISPLAY);
  const hasMore = activity.length > INITIAL_DISPLAY;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Recent Activity
      </Typography>
      {activity.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <MessageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No recent activity on your cases
          </Typography>
        </Box>
      ) : (
        <>
          <List disablePadding dense>
            {visibleActivity.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => onCaseClick(item.caseId)}
                sx={{ borderRadius: 1, px: 1, py: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>
                      <Typography component="span" variant="body2" fontWeight={600}>
                        {item.authorName}
                      </Typography>
                      {' '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {item.title}
                      </Typography>
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        color="primary.main"
                        noWrap
                        sx={{ '&:hover': { textDecoration: 'underline' } }}
                      >
                        {item.caseTitle}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, ml: 1 }}>
                        {formatRelativeTime(item.date)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setExpanded((prev) => !prev)}>
                {expanded ? 'Show Less' : `Show All (${activity.length})`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

export default RecentActivitySection;
